import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { environment } from '@app/environments/environment';
import { DeleteCreativeDialogComponent } from '@app/manage-view/delete-creative-dialog';
import {
    ICampaignPublishInfo,
    ICampaignPublishStatusResponse,
    IPublishInfo
} from '@studio/domain/campaign';
import { EnvironmentService } from '@app/shared/services/environment.service';
import { UIDialogService } from '@bannerflow/ui';
import { ICreative } from '@studio/domain/creativeset/creative/creative';
import { ICreativeset } from '@studio/domain/creativeset/creativeset';
import { Logger } from '@studio/utils/logger';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { exhaustMap, filter, map, skip, take, takeUntil, tap } from 'rxjs/operators';
import { CreativesetDataService } from '../creativeset/creativeset.data.service';
import { EditCreativeService } from '@app/manage-view/services';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable({ providedIn: 'root' })
export class CampaignService implements OnDestroy {
    private creativeset: ICreativeset;
    private logger = new Logger('CampaignService');
    private _creativesPublished$ = new BehaviorSubject<IPublishInfo[]>([]);
    creativesPublished$ = this._creativesPublished$.asObservable();
    private publishedCreatives: IPublishInfo[] = [];
    private fetchCampaigns$ = new Subject<void>();

    private unsubscribe$ = new Subject<void>();

    dialogIsOpen = false;

    constructor(
        private creativesetDataService: CreativesetDataService,
        private environmentService: EnvironmentService,
        private dialogService: UIDialogService,
        private http: HttpClient,
        private editCreativeService: EditCreativeService
    ) {
        this.logger.verbose('Initializing');

        this.creativesetDataService.creativeset$
            .pipe(takeUntilDestroyed())
            .subscribe(creativeset => (this.creativeset = creativeset));

        if (this.environmentService.inShowcaseMode) {
            return;
        }

        this.editCreativeService.isUpdatingChecksums$
            .pipe(
                skip(1),
                filter(isUpdating => !isUpdating)
            )
            .subscribe(() => {
                this.fetchCampaignStatus();
            });

        this.fetchCampaigns$
            .pipe(
                takeUntil(this.unsubscribe$),
                filter(
                    () =>
                        !!this.creativeset.creatives.find(creative =>
                            this.isCreativeConnectedToCampaign(creative)
                        )
                ),
                exhaustMap(() => this.makeCampaignRequest())
            )
            .subscribe(/** Empty subscription to trigger this. Side effect occurs in makeCampaignRequest */);
    }

    makeCampaignRequest(): Observable<IPublishInfo[]> {
        const ids = this.getCampaignIds();
        const url = this.getCampaignServiceOrigin(ids);
        return this.http.get<ICampaignPublishStatusResponse[]>(url).pipe(
            map(statuses => this.mapCampaignAndCreativeInfo(statuses)),
            tap(statuses => this.updateCreativePublishInfo(statuses))
        );
    }

    updateCreativePublishInfo(publishedInfos: IPublishInfo[]): void {
        this.publishedCreatives = publishedInfos;
        this._creativesPublished$.next(publishedInfos);
    }

    private getCampaignManagerOrigin(): string {
        return `${
            environment.origins.campaignManager
        }/${this.creativesetDataService.getAccountSlug()}/${this.creativesetDataService.getBrandSlug()}`;
    }

    private getCampaignServiceOrigin(campaignIds: string[]): string {
        return `${
            environment.origins.campaignService
        }/api/${this.creativesetDataService.getAccountSlug()}/${this.creativesetDataService.getBrandSlug()}/campaign/status?ids=${campaignIds.join(
            '&ids='
        )}`;
    }

    private mapCampaignAndCreativeInfo(
        currentStatuses: ICampaignPublishStatusResponse[]
    ): IPublishInfo[] {
        const creativesWithCampaigns = this.getCreativesWithCampaigns();
        const publishedInfos: IPublishInfo[] = [];
        for (const creative of creativesWithCampaigns) {
            const connectedCampaigns = currentStatuses
                .filter(publishStatus =>
                    publishStatus.creatives.find(c => c.creativeId === creative.id)
                )
                .map(publishStatus => {
                    const campaignCreative = publishStatus.creatives.find(
                        c => c.creativeId === creative.id
                    )!;
                    const { failed, generated, checksum, publishOptionsSupportHeavyVideo } =
                        campaignCreative;

                    const status: ICampaignPublishInfo = {
                        id: publishStatus.id,
                        name: publishStatus.name,
                        checksum,
                        publishStatus: {
                            failed,
                            generated,
                            status: publishStatus.status,
                            publishOptionsSupportHeavyVideo
                        }
                    };

                    return status;
                });

            const publishedCreative = this.publishedCreatives.find(pc => pc.creative === creative);

            publishedInfos.push({
                creative,
                loading: publishedCreative?.loading || false,
                connectedCampaigns
            });
        }

        return publishedInfos;
    }

    fetchCampaignStatus = (): void => {
        this.fetchCampaigns$.next();
    };

    private getCampaignIds(): string[] {
        const creativesWithCampaigns = this.getCreativesWithCampaigns();

        return creativesWithCampaigns.reduce<string[]>((acc, creative) => {
            for (const campaignId of creative.connectedCampaigns || []) {
                acc = acc.includes(campaignId) ? acc : [...acc, campaignId];
            }
            return acc;
        }, []);
    }

    redirectToCreateCampaign(): void {
        window.open(this.getCreateCampaignUrlFromCreativeset(), '_blank');
    }

    redirectToCreateSocialCampaign(): void {
        window.open(
            `${
                environment.origins.socialCampaignManager
            }/${this.creativesetDataService.getAccountSlug()}/${this.creativesetDataService.getBrandSlug()}/creativeset/${
                this.creativeset.id
            }`,
            '_blank'
        );
    }

    connectedToCampaignDialog(creatives: ICreative[]): void {
        if (this.dialogIsOpen) {
            return;
        }

        const campaigns = this.getCampaignPublishInfo(creatives);
        this.dialogIsOpen = true;
        const ref = this.dialogService.openComponent(DeleteCreativeDialogComponent, {
            headerText: 'Creative in use by campaign',
            theme: 'default',
            width: '700px',
            data: { campaigns }
        });

        ref.afterClose()
            .pipe(take(1))
            .subscribe(() => {
                this.dialogIsOpen = false;
            });
    }

    getCampaignPublishInfo(creatives: ICreative[]): ICampaignPublishInfo[] {
        const campaigns = creatives
            .filter(
                creative =>
                    this.isCreativeConnectedToCampaign(creative) &&
                    this.publishedCreatives.some(pc => pc.creative.id === creative.id)
            )
            .map(creative => this.getCreativePublishInfo(creative)!.connectedCampaigns)
            .reduce((acc, curr) => [...acc, ...curr], []);

        const distinctCampaigns: ICampaignPublishInfo[] = [];

        for (const campaign of campaigns) {
            if (distinctCampaigns.some(dc => dc.id === campaign.id)) {
                continue;
            }

            distinctCampaigns.push(campaign);
        }

        return distinctCampaigns;
    }

    getCampaigns(creatives?: ICreative[]): ICampaignPublishInfo[] {
        if (creatives && !Array.isArray(creatives)) {
            creatives = [creatives];
        }

        if (creatives) {
            return this.getCampaignPublishInfo(creatives);
        } else {
            const allCreatives = this.creativesetDataService.creativeset.creatives.map(c => c);
            return this.getCampaignPublishInfo(allCreatives);
        }
    }

    isCreativesConnectedToCampaigns(creatives: ICreative[]): boolean {
        for (const creative of creatives) {
            const isConnected = this.isCreativeConnectedToCampaign(creative);

            if (isConnected) {
                this.connectedToCampaignDialog([creative]);
                return true;
            }
        }

        return false;
    }

    isCreativeConnectedToCampaign(creative: ICreative): boolean {
        if (!creative?.connectedCampaigns?.length) {
            return false;
        }

        if (!creative.checksum || !creative.design) {
            return false;
        }

        return true;
    }

    getCreativePublishInfo(creative: ICreative): IPublishInfo | undefined {
        return this.publishedCreatives.find(
            publishedCreative => publishedCreative.creative.id === creative.id
        );
    }

    private getCreativesWithCampaigns(): ICreative[] {
        return [...this.creativesetDataService.creativeset.creatives].filter(creative => {
            if (creative.connectedCampaigns && creative.connectedCampaigns.length) {
                return true;
            }
            return false;
        });
    }

    openCampaign(campaignId: string): void {
        const url = this.getCampaignUrl(campaignId);
        window.open(url, '_blank');
    }

    private getCampaignUrl(campaignId: string): Readonly<string> {
        return `${this.getCampaignManagerOrigin()}/campaign/${campaignId}/list`;
    }

    private getCreateCampaignUrlFromCreativeset(): Readonly<string> {
        const creativeset = this.creativesetDataService.creativeset;

        return `${this.getCampaignManagerOrigin()}/creativeset/${creativeset.id}`;
    }

    addToCampaign(campaignId: string, creatives: string[], creativeSetId: string): void {
        const cmWindow = window.open(
            `${this.getCampaignManagerOrigin()}/campaign/${campaignId}/add-creatives`
        );

        if (cmWindow === null) {
            throw new Error('CM Window has failed to open.');
        }

        const eventListener = (event: MessageEvent<string>): void => {
            if (
                event.origin === `${environment.origins.campaignManager || ''}` &&
                event.data === 'CM_LOADED'
            ) {
                cmWindow.postMessage(
                    { creatives, creativeSetId },
                    `${environment.origins.campaignManager}`
                );
                window.removeEventListener('message', eventListener);
            }
        };

        window.addEventListener('message', eventListener);
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }
}
